import * as React from 'react';
import { v4 as getUuid } from 'uuid';
import styled from 'styled-components';
import Switcher from '@sdk/components/forms/Switcher';
import View from '@sdk/components/primitives/View';
import DisplayText from '@sdk/components/ui/DisplayText';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { SpacingType } from '@sdk/components/ui/spacing/SpacingStyle';
import { SmartTooltip } from '@sdk/common/tooltip/SmartTooltip';

import PremiumGuardLabel from '../../premiumGuard/PremiumGuardLabel';
import { PremiumGuardPassedConfig } from '../../premiumGuard/PremiumGuard';

import RowSpaceBetween from './RowSpaceBetween';
import SettingsSection from './SettingsSection';

type StyledRowProps = {
  withMainHeader?: boolean;
};

const StyledRow = styled(Row)<StyledRowProps>`
  align-items: center;
  ${({ withMainHeader }): string => withMainHeader && 'padding-bottom: 5px'};
`;

type Props = {
  disabled?: boolean;
  title?: string;
  subtitle?: string;
  value: boolean;
  withSeparator?: boolean;
  withMainHeader?: boolean;
  subtitleItem?: JSX.Element;
  onChange: (value: boolean) => void;
  premiumGuard?: PremiumGuardPassedConfig;
  disabledTip?: string;
} & SpacingType;

const SettingsSwitcherRow: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  subtitle,
  subtitleItem,
  withMainHeader,
  onChange,
  value,
  withSeparator,
  premiumGuard,
  disabled,
  disabledTip,
  ...props
}) => {
  return (
    <SettingsSection withSeparator={withSeparator} {...props}>
      <RowSpaceBetween>
        <View>
          <Column>
            <StyledRow withMainHeader={withMainHeader}>
              <DisplayText
                semiBold
                fontSize={withMainHeader ? 16 : 14}
                themeColor={withMainHeader ? ThemeColorType.primary : ThemeColorType.gray}>
                {title}
              </DisplayText>
              {premiumGuard && !premiumGuard.hasAccess && (
                <PremiumGuardLabel
                  pLeft={10}
                  label={premiumGuard.requiredPlan}
                  tooltipText={premiumGuard.tooltipContent}
                  tooltipId={premiumGuard.tooltipId}
                />
              )}
            </StyledRow>
            {!!subtitle && <Text14 themeColor={ThemeColorType.gray}>{subtitle}</Text14>}
            {!!subtitleItem && subtitleItem}
          </Column>
        </View>
        <Row pLeft={80}>
          <SmartTooltip content={disabledTip} disable={!(disabled && disabledTip)} data-place="top">
            <span>
              <Switcher small value={value} onChange={onChange} disabled={disabled} />
            </span>
          </SmartTooltip>
        </Row>
      </RowSpaceBetween>
    </SettingsSection>
  );
};

export default SettingsSwitcherRow;
